import type {
  User,
  AdminUser,
  AdminUserDetails,
  UserExchange,
  UserTransferDetail,
  Exchange,
  ArbitragePair,
  Circle,
  CircleStats,
  CreateCircleRequest,
  Announcement,
  AuthResponse,
  CreatePairRequest,
  CreateAdminCircleRequest,
  AdminCircle,
  WalletTransaction,
  AdminStats,
  VerificationWithUser,
  WalletApiHealth,
  AuditLogEntry,
} from '@/types';

const API_BASE = 'https://tg-crypto-arbitrage-api.zzjjdkas2.workers.dev/api';
const ADMIN_TOKEN_KEY = 'adminSessionToken';
const ADMIN_AUTH_CHANGED_EVENT = 'admin-auth-changed';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export function isUnauthorizedError(error: unknown) {
  return error instanceof ApiError && error.status === 401;
}

function notifyAdminAuthChanged() {
  window.dispatchEvent(new Event(ADMIN_AUTH_CHANGED_EVENT));
}

export function addAdminAuthChangeListener(listener: () => void) {
  window.addEventListener(ADMIN_AUTH_CHANGED_EVENT, listener);
  return () => window.removeEventListener(ADMIN_AUTH_CHANGED_EVENT, listener);
}

export function getApiUrl(path: string) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

export function getStoredAdminToken() {
  return window.localStorage.getItem(ADMIN_TOKEN_KEY);
}

export function storeAdminToken(token: string) {
  window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
  notifyAdminAuthChanged();
}

export function clearAdminToken() {
  window.localStorage.removeItem(ADMIN_TOKEN_KEY);
  notifyAdminAuthChanged();
}

// Helper to get auth header
function getAuthHeader(): HeadersInit {
  // In Telegram environment, we'd use initData
  const initData = window.Telegram?.WebApp?.initData;
  const adminToken = getStoredAdminToken();
  return {
    'Content-Type': 'application/json',
    ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
    ...(adminToken ? { Authorization: `Bearer ${adminToken}` } : {}),
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    const message = error.error || 'Request failed';

    if (response.status === 401 && new URL(response.url).pathname.includes('/api/admin')) {
      clearAdminToken();
    }

    throw new ApiError(message, response.status);
  }
  return response.json();
}

// ============== AUTH ==============

export interface AdminSession {
  admin: {
    username: string | null;
    role: 'admin' | 'super_admin';
  };
}

export async function loginAdmin(username: string, password: string): Promise<AdminSession & { token: string }> {
  const response = await fetch(`${API_BASE}/auth/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  return handleResponse(response);
}

export async function getAdminSession(): Promise<AdminSession> {
  const response = await fetch(`${API_BASE}/auth/admin/me`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function authenticate(mockUser?: { id: string; username?: string; first_name?: string; last_name?: string }): Promise<AuthResponse> {
  const initData = window.Telegram?.WebApp?.initData;
  
  const response = await fetch(`${API_BASE}/auth/telegram`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      initData,
      mockUser,
    }),
  });
  
  return handleResponse<AuthResponse>(response);
}

export async function getMe(): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/auth/me`, {
    headers: getAuthHeader(),
  });
  return handleResponse<AuthResponse>(response);
}

// ============== USER ==============

export async function getUserProfile(): Promise<{ user: User; exchanges: UserExchange[]; verifiedExchangeCount: number }> {
  const response = await fetch(`${API_BASE}/user/profile`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function updateBudget(budget: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/user/budget`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ budget }),
  });
  return handleResponse(response);
}

export async function updateProfitGoal(monthly_profit_goal: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/user/profit-goal`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ monthly_profit_goal }),
  });
  return handleResponse(response);
}

export async function completeOnboarding(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/user/onboarding`, {
    method: 'PUT',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getUserExchanges(): Promise<{ exchanges: UserExchange[] }> {
  const response = await fetch(`${API_BASE}/user/exchanges`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function submitExchangeUID(exchange_id: string, exchange_uid: string): Promise<{ verification: UserExchange }> {
  const response = await fetch(`${API_BASE}/user/exchanges`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify({ exchange_id, exchange_uid }),
  });
  return handleResponse(response);
}

export async function uploadScreenshot(verificationId: number, file: File): Promise<{ success: boolean; screenshot_url: string }> {
  const formData = new FormData();
  formData.append('screenshot', file);
  
  const headers: HeadersInit = {};
  const initData = window.Telegram?.WebApp?.initData;
  const adminToken = getStoredAdminToken();
  if (initData) {
    headers['X-Telegram-Init-Data'] = initData;
  }
  if (adminToken) {
    headers.Authorization = `Bearer ${adminToken}`;
  }
  
  const response = await fetch(`${API_BASE}/user/exchanges/${verificationId}/screenshot`, {
    method: 'POST',
    headers,
    body: formData,
  });
  return handleResponse(response);
}

export async function fetchVerificationScreenshot(screenshotKey: string): Promise<Blob> {
  const normalizedKey = screenshotKey
    .replace(/^https?:\/\/[^/]+\/api\/screenshots\//, '')
    .replace(/^\/?api\/screenshots\//, '')
    .replace(/^\/+/, '');
  const encodedKey = normalizedKey.split('/').map(encodeURIComponent).join('/');
  const response = await fetch(getApiUrl(`/screenshots/${encodedKey}`), {
    headers: getAuthHeader(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to load screenshot' }));
    throw new Error(error.error || 'Failed to load screenshot');
  }

  return response.blob();
}

export async function getUserTransfers(): Promise<{ transfers: UserTransferDetail[] }> {
  const response = await fetch(`${API_BASE}/user/transfers`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// ============== EXCHANGES ==============

export async function getExchanges(): Promise<{ exchanges: Exchange[] }> {
  const response = await fetch(`${API_BASE}/exchanges`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// ============== PAIRS ==============

export interface GetPairsParams {
  coin?: string;
  exchange?: string;
  minSpread?: number;
  maxSpread?: number;
  status?: 'active' | 'inactive' | 'all';
  type?: 'single' | 'double' | 'all';
  sort?: 'spread_asc' | 'spread_desc' | 'display_order';
}

export async function getPairs(params?: GetPairsParams): Promise<{ pairs: ArbitragePair[] }> {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
  }
  
  const response = await fetch(`${API_BASE}/pairs?${searchParams}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getPairById(id: number): Promise<{ pair: ArbitragePair; transfer_details: UserTransferDetail[] }> {
  const response = await fetch(`${API_BASE}/pairs/${id}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function updatePairNote(pairId: number, note: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/pairs/${pairId}/note`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ note }),
  });
  return handleResponse(response);
}

export async function deletePairNote(pairId: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/pairs/${pairId}/note`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// ============== NOTIFICATIONS ==============

export async function updateNotificationPreference(pairId: number, bot_enabled: boolean): Promise<{ success: boolean; pair_id: number; bot_enabled: boolean }> {
  const response = await fetch(`${API_BASE}/notifications/preferences/${pairId}`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ bot_enabled }),
  });
  return handleResponse(response);
}

// ============== CIRCLES ==============

export async function getCircles(limit = 50, offset = 0): Promise<{ circles: Circle[]; total: number }> {
  const response = await fetch(`${API_BASE}/circles?limit=${limit}&offset=${offset}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getCircleStats(): Promise<{ stats: CircleStats; budget: number }> {
  const response = await fetch(`${API_BASE}/circles/stats`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function createCircle(data: CreateCircleRequest): Promise<{ circle: Circle }> {
  const response = await fetch(`${API_BASE}/circles`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function deleteCircle(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/circles/${id}`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function exportCircles(): Promise<Blob> {
  const response = await fetch(`${API_BASE}/circles/export`, {
    headers: getAuthHeader(),
  });
  if (!response.ok) {
    throw new Error('Export failed');
  }
  return response.blob();
}

// ============== ANNOUNCEMENTS ==============

export async function getAnnouncements(): Promise<{ announcements: Announcement[]; unread_count: number }> {
  const response = await fetch(`${API_BASE}/announcements`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function markAnnouncementsRead(): Promise<{ success: boolean; unread_count: number }> {
  const response = await fetch(`${API_BASE}/announcements/read-all`, {
    method: 'POST',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// ============== ADMIN ANNOUNCEMENTS ==============

export interface AdminAnnouncementInput {
  title: string;
  content: string;
  is_active?: boolean;
  expires_at?: string | null;
}

export async function getAdminAnnouncements(): Promise<{ announcements: Announcement[] }> {
  const response = await fetch(`${API_BASE}/admin/announcements`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function createAnnouncement(data: AdminAnnouncementInput): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/announcements`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function updateAnnouncement(
  id: number,
  data: Partial<AdminAnnouncementInput>
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/announcements/${id}`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function deleteAnnouncement(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/announcements/${id}`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// ============== ADMIN ==============

export async function getAdminPairs(): Promise<{ pairs: ArbitragePair[] }> {
  const response = await fetch(`${API_BASE}/admin/pairs`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function createPair(data: CreatePairRequest): Promise<{ pair: ArbitragePair }> {
  const response = await fetch(`${API_BASE}/admin/pairs`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function updatePair(id: number, data: Partial<CreatePairRequest> & { display_order?: number }): Promise<{ pair: ArbitragePair }> {
  const response = await fetch(`${API_BASE}/admin/pairs/${id}`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function deletePair(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/pairs/${id}`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function duplicatePair(id: number): Promise<{ pair: ArbitragePair }> {
  const response = await fetch(`${API_BASE}/admin/pairs/${id}/duplicate`, {
    method: 'POST',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function bulkUpdatePairStatus(pair_ids: number[], is_active: boolean): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/pairs/bulk-status`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ pair_ids, is_active }),
  });
  return handleResponse(response);
}

export async function getAdminCircles(status?: 'active' | 'stopped'): Promise<{ circles: AdminCircle[] }> {
  const params = status ? `?status=${status}` : '';
  const response = await fetch(`${API_BASE}/admin/circles${params}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function createAdminCircle(data: CreateAdminCircleRequest): Promise<{ circle: AdminCircle }> {
  const response = await fetch(`${API_BASE}/admin/circles`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function stopAdminCircle(id: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/circles/${id}/stop`, {
    method: 'POST',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getAdminCircleTransactions(id: number): Promise<{ transactions: WalletTransaction[] }> {
  const response = await fetch(`${API_BASE}/admin/circles/${id}/transactions`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getVerifications(status = 'pending'): Promise<{ verifications: VerificationWithUser[] }> {
  const response = await fetch(`${API_BASE}/admin/verifications?status=${status}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function decideVerification(id: number, status: 'verified' | 'rejected', rejection_reason?: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/verifications/${id}`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ status, rejection_reason }),
  });
  return handleResponse(response);
}

export async function getAdminStats(): Promise<{ stats: AdminStats }> {
  const response = await fetch(`${API_BASE}/admin/stats`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getWalletApiHealth(): Promise<{ ok: boolean; checked_at: string; apis: WalletApiHealth[] }> {
  const response = await fetch(`${API_BASE}/admin/wallet-apis/health`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getAuditLog(limit = 100, offset = 0): Promise<{ audit_log: AuditLogEntry[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetch(`${API_BASE}/admin/audit-log?${params}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getAdminUsers(limit = 50, offset = 0, search?: string): Promise<{ users: AdminUser[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (search) params.append('search', search);
  
  const response = await fetch(`${API_BASE}/admin/users?${params}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function getAdminUserDetails(userId: number): Promise<AdminUserDetails> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function updateAdminUser(
  userId: number,
  data: { access_level: 'basic' | 'private'; admin_notes?: string | null }
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function updateUserAccess(userId: number, access_level: 'basic' | 'private'): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/access`, {
    method: 'PUT',
    headers: getAuthHeader(),
    body: JSON.stringify({ access_level }),
  });
  return handleResponse(response);
}

export async function getUserTransferDetails(userId: number): Promise<{ transfers: UserTransferDetail[] }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/transfers`, {
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

export async function createUserTransferDetail(
  userId: number,
  data: { pair_id: number | ''; deposit_address?: string; memo?: string; network?: string; notes?: string }
): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/transfers`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function createUserSteemWallet(
  userId: number,
  data: { pair_id: number | ''; account_name?: string; network?: string; notes?: string }
): Promise<{ success: boolean; wallet: { id: number | null; account: string; network: string; provider: string }; transfer: UserTransferDetail }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/steem-wallets`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function createUserHiveWallet(
  userId: number,
  data: { pair_id: number | ''; account_name?: string; network?: string; notes?: string }
): Promise<{ success: boolean; wallet: { id: number | null; account: string; network: string; provider: string }; transfer: UserTransferDetail }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/hive-wallets`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: JSON.stringify(data),
  });
  return handleResponse(response);
}

export async function deleteUserTransferDetail(userId: number, transferId: number): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/transfers/${transferId}`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  return handleResponse(response);
}

// Telegram WebApp type declaration
declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
          };
        };
        version?: string;
        platform?: string;
        viewportHeight?: number;
        viewportStableHeight?: number;
        safeAreaInset?: {
          top: number;
          right: number;
          bottom: number;
          left: number;
        };
        contentSafeAreaInset?: {
          top: number;
          right: number;
          bottom: number;
          left: number;
        };
        ready: () => void;
        expand: () => void;
        requestFullscreen?: () => void;
        close: () => void;
        isVersionAtLeast?: (version: string) => boolean;
        setBottomBarColor?: (color: string) => void;
        onEvent?: (eventType: string, eventHandler: () => void) => void;
        offEvent?: (eventType: string, eventHandler: () => void) => void;
        MainButton: {
          text: string;
          show: () => void;
          hide: () => void;
          onClick: (fn: () => void) => void;
        };
        BackButton: {
          show: () => void;
          hide: () => void;
          onClick: (fn: () => void) => void;
        };
        themeParams: {
          bg_color?: string;
          text_color?: string;
          hint_color?: string;
          link_color?: string;
          button_color?: string;
          button_text_color?: string;
        };
      };
    };
  }
}
